/**
 * Read-only access to the media-archive session.
 *
 * The archive pages (/login.html, /gallery.html, /album.html) and their auth module (/app.js) are
 * served by the backend but proxied under this app's origin — see frontend/nginx.conf and the dev
 * proxy in vite.config.ts. Firebase persists its session in localStorage, which is per-origin, so
 * a user who has signed in at /login.html already has a session visible to this app.
 *
 * This module therefore does not implement sign-in. It loads /app.js at runtime and reads the
 * session that is already there, which keeps a single Firebase SDK version, a single
 * initializeApp() call, and a single storage key across both UIs. Duplicating the setup with an
 * npm-installed SDK would only share that session for as long as the two copies agreed.
 *
 * Every path degrades to "anonymous" rather than throwing: Firebase may not be configured on the
 * server at all, and an anonymous download is a first-class case here, not an error.
 */

// Resolved at runtime, not by Vite: /app.js is served by the backend, not built by this app.
const AUTH_MODULE_URL = '/app.js'

export interface ArchiveUser {
  uid: string
  email: string | null
  getIdToken(): Promise<string>
}

interface ArchiveAuth {
  currentUser: ArchiveUser | null
  authStateReady(): Promise<void>
  onAuthStateChanged(callback: (user: ArchiveUser | null) => void): () => void
}

interface ArchiveAuthModule {
  initFirebase(): Promise<ArchiveAuth>
}

let authPromise: Promise<ArchiveAuth | null> | null = null

async function loadAuth(): Promise<ArchiveAuth | null> {
  try {
    const module = (await import(/* @vite-ignore */ AUTH_MODULE_URL)) as ArchiveAuthModule
    const auth = await module.initFirebase()
    // Resolves once the SDK has finished restoring any persisted session, so callers never see a
    // spurious null from reading currentUser too early.
    await auth.authStateReady()
    return auth
  } catch {
    return null
  }
}

function getAuth(): Promise<ArchiveAuth | null> {
  if (!authPromise) {
    authPromise = loadAuth()
  }
  return authPromise
}

/**
 * Authorization header for the signed-in user, or an empty object when signed out or unavailable.
 * Safe to spread into any fetch init.
 */
export async function getAuthHeader(): Promise<Record<string, string>> {
  const auth = await getAuth()
  const user = auth?.currentUser
  if (!user) return {}

  try {
    return { Authorization: `Bearer ${await user.getIdToken()}` }
  } catch {
    return {}
  }
}

/** The signed-in user, or null. */
export async function getArchiveUser(): Promise<ArchiveUser | null> {
  const auth = await getAuth()
  return auth?.currentUser ?? null
}

/**
 * Subscribes to sign-in changes. Returns an unsubscribe function; calling it before the auth
 * module has loaded cancels the pending subscription.
 */
export function subscribeToArchiveUser(callback: (user: ArchiveUser | null) => void): () => void {
  let unsubscribe: (() => void) | null = null
  let cancelled = false

  void getAuth().then((auth) => {
    if (cancelled) return
    if (!auth) {
      callback(null)
      return
    }
    unsubscribe = auth.onAuthStateChanged(callback)
  })

  return () => {
    cancelled = true
    unsubscribe?.()
  }
}
